import {
	Button,
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@ancatag/at-design";
import {
	AlertCircle,
	ArrowLeft,
	FileText,
	Loader2,
	Upload,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { useLocation, useRoute } from "wouter";
import { CollectionSettings } from "../../components/rag/CollectionSettings";
import { DocumentList } from "../../components/rag/DocumentList";
import { DocumentUpload } from "../../components/rag/DocumentUpload";
import { RagStats } from "../../components/rag/RagStats";
import {
	type RagCollection as RagCollectionType,
	type RagDocument,
	ragService,
} from "../../services/rag";
import { getRouteConfig } from "../../services/route-configs";

export function RagCollection() {
	const [, setLocation] = useLocation();
	const [, params] = useRoute("/route-configs/:id/rag");
	const routeConfigId = params?.id || "";

	const [collection, setCollection] = useState<RagCollectionType | null>(null);
	const [documents, setDocuments] = useState<RagDocument[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	const loadData = useCallback(async () => {
		if (!routeConfigId) return;

		try {
			setLoading(true);
			setError(null);

			// First, check if RAG is enabled for this RouteConfig
			const routeConfig = await getRouteConfig(routeConfigId);

			if (!routeConfig.ragEnabled) {
				setError("RAG is not enabled for this RouteConfig");
				setLoading(false);
				return;
			}

			// Load collection and documents if RAG is enabled
			const [collectionData, documentsData] = await Promise.all([
				ragService.getCollection(routeConfigId).catch(() => null),
				ragService
					.listDocuments(routeConfigId)
					.catch(() => ({ documents: [], total: 0 })),
			]);

			setCollection(collectionData);
			setDocuments(documentsData.documents);
		} catch (err) {
			const message =
				err instanceof Error ? err.message : "Failed to load RAG collection";
			setError(message);
			toast.error(message);
		} finally {
			setLoading(false);
		}
	}, [routeConfigId]);

	useEffect(() => {
		loadData();
	}, [loadData]);

	// Poll for processing documents
	useEffect(() => {
		if (!routeConfigId) return;

		const hasProcessing = documents.some(
			(doc) => doc.status === "pending" || doc.status === "processing",
		);

		if (!hasProcessing) return;

		const interval = setInterval(() => {
			loadData();
		}, 3000); // Poll every 3 seconds

		return () => clearInterval(interval);
	}, [documents, routeConfigId, loadData]);

	const handleUploadComplete = () => {
		loadData();
	};

	const handleDeleteDocument = async (documentId: string) => {
		try {
			await ragService.deleteDocument(documentId);
			toast.success("Document deleted");
			loadData();
		} catch (err) {
			const message =
				err instanceof Error ? err.message : "Failed to delete document";
			toast.error(message);
		}
	};

	const handleReprocessDocument = async (documentId: string) => {
		try {
			await ragService.reprocessDocument(documentId);
			toast.success("Document reprocessing started");
			loadData();
		} catch (err) {
			const message =
				err instanceof Error ? err.message : "Failed to reprocess document";
			toast.error(message);
		}
	};

	const handleUpdateSettings = async (settings: any) => {
		if (!routeConfigId) return;

		try {
			const updated = await ragService.updateCollectionSettings(
				routeConfigId,
				settings,
			);
			setCollection(updated);
			toast.success("Settings updated");
		} catch (err) {
			const message =
				err instanceof Error ? err.message : "Failed to update settings";
			toast.error(message);
		}
	};

	if (loading && !collection) {
		return (
			<div className="flex items-center justify-center min-h-[400px]">
				<Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
			</div>
		);
	}

	if (error && !collection) {
		return (
			<div className="p-6">
				<Card>
					<CardHeader>
						<CardTitle className="flex items-center gap-2 text-destructive">
							<AlertCircle className="h-5 w-5" />
							RAG Not Enabled
						</CardTitle>
						<CardDescription>
							RAG (Retrieval-Augmented Generation) must be enabled for this
							RouteConfig before you can manage documents.
						</CardDescription>
					</CardHeader>
					<CardContent className="space-y-4">
						<p className="text-sm text-muted-foreground">To enable RAG:</p>
						<ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
							<li>Go to the RouteConfig editor</li>
							<li>Enable the "RAG" toggle in the RAG Settings section</li>
							<li>Configure the embedding provider and model (optional)</li>
							<li>Save the RouteConfig</li>
						</ol>
						<div className="flex gap-2 pt-4">
							<Button
								onClick={() =>
									setLocation(`/route-configs/${routeConfigId}/edit`)
								}
							>
								Enable RAG in Route Config
							</Button>
							<Button variant="outline" onClick={() => setLocation("/models")}>
								<ArrowLeft className="mr-2 h-4 w-4" />
								Back to Models
							</Button>
						</div>
					</CardContent>
				</Card>
			</div>
		);
	}

	return (
		<div className="p-6 space-y-6">
			{/* Header */}
			<div className="flex items-center justify-between">
				<div className="flex items-center gap-4">
					<Button
						variant="ghost"
						size="icon"
						onClick={() => setLocation(`/route-configs/${routeConfigId}/edit`)}
					>
						<ArrowLeft className="h-4 w-4" />
					</Button>
					<div>
						<h1 className="text-3xl font-bold tracking-tight">RAG Documents</h1>
						<p className="text-muted-foreground">
							Manage documents for retrieval-augmented generation
						</p>
					</div>
				</div>
			</div>

			{/* Stats */}
			{collection && <RagStats collection={collection} />}

			{/* Upload Section */}
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center gap-2">
						<Upload className="h-5 w-5" />
						Upload Documents
					</CardTitle>
					<CardDescription>
						Upload PDF, TXT, or Markdown files to add to your RAG collection
					</CardDescription>
				</CardHeader>
				<CardContent>
					<DocumentUpload
						routeConfigId={routeConfigId}
						onUploadComplete={handleUploadComplete}
					/>
				</CardContent>
			</Card>

			{/* Document List */}
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center gap-2">
						<FileText className="h-5 w-5" />
						Documents
					</CardTitle>
					<CardDescription>
						{documents.length === 0
							? "No documents uploaded yet"
							: `${documents.length} document${documents.length === 1 ? "" : "s"} in collection`}
					</CardDescription>
				</CardHeader>
				<CardContent>
					<DocumentList
						documents={documents}
						onDelete={handleDeleteDocument}
						onReprocess={handleReprocessDocument}
					/>
				</CardContent>
			</Card>

			{/* Settings */}
			{collection && (
				<CollectionSettings
					collection={collection}
					onUpdate={handleUpdateSettings}
				/>
			)}
		</div>
	);
}
